/* eslint-disable react/prop-types */
import { Pause, Send } from 'lucide-react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Tooltip from '../Common/ToolTip';
import {
  getInputShellClasses,
  getPrimaryButtonClasses,
} from '../../utils/ai/chatTheme';

const ChatInput = (props) => {
  const {
    onSend,
    onPause,
    isGenerating = false,
    disabled = false,
    placeholder = 'Type a message...',
    buttonLabel = 'Send',
    theme = 'dark',
    value: controlledValue,
    onChange,
    autoFocus = false,
  } = props;

  const [internalValue, setInternalValue] = useState(controlledValue || '');
  const isControlled = typeof controlledValue === 'string';
  const value = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue, isControlled]);

  const updateValue = (nextValue) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }

    if (onChange) {
      onChange(nextValue);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isGenerating) {
      return;
    }

    const trimmed = String(value || '').trim();

    if (!trimmed || disabled) {
      return;
    }

    onSend?.(trimmed);

    if (!isControlled) {
      setInternalValue('');
    }

    if (onChange) {
      onChange('');
    }
  };

  const inputStyles =
    theme === 'dark'
      ? {
          color: '#C8C8C8',
          caretColor: '#FFFFFF',
          WebkitTextFillColor: '#C8C8C8',
          backgroundColor: '#1B2D42',
        }
      : {
          color: '#202020',
          caretColor: '#066FD1',
          WebkitTextFillColor: '#202020',
          backgroundColor: '#FFFFFF',
        };

  const shell = getInputShellClasses(theme);
  const canSend = Boolean(String(value || '').trim()) && !disabled && !isGenerating;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex items-center gap-2">
        <input
          autoFocus={autoFocus}
          type="text"
          value={value}
          onChange={(event) => updateValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSubmit(event);
            }
          }}
          placeholder={placeholder}
          disabled={disabled || isGenerating}
          spellCheck={false}
          className={`min-h-10 w-full rounded-md border px-3 py-2.5 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[#066FD1]/25 ${shell} ${
            disabled || isGenerating ? 'cursor-not-allowed opacity-90' : ''
          }`}
          style={inputStyles}
        />

        {isGenerating ? (
          <Tooltip content="Pause" position="top">
            <button
              type="button"
              onClick={() => onPause?.()}
              className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#066FD1]/30 ${getPrimaryButtonClasses(
                theme,
                false,
              )}`}
              aria-label="Pause"
            >
              <Pause className="h-4 w-4" />
            </button>
          </Tooltip>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#066FD1]/30 ${getPrimaryButtonClasses(
              theme,
              !canSend,
            )}`}
            aria-label={buttonLabel}
          >
            <Send className="h-4 w-4" />
          </button>
        )}
      </div>
    </form>
  );
};

ChatInput.propTypes = {
  onSend: PropTypes.func,
  onPause: PropTypes.func,
  isGenerating: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  buttonLabel: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  value: PropTypes.string,
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
};

export default ChatInput;
